"use client";

import { useEffect, useRef, useState } from "react";

function useAnimateOnScroll() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, visible };
}

export function ProblemSection() {
  const { ref, visible: show } = useAnimateOnScroll();
  return (
    <section id="problem" ref={ref} className="py-24 bg-white">
      <div className="max-w-5xl mx-auto px-6">
        <div className={`text-center mb-16 transition-all duration-700 ${show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl text-[#1B3A6B] mb-4">
            Your associates are drowning in contracts.<br />
            <span className="text-[#C8A84B]">Your firm is leaving money on the table.</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-12">
          {[
            { stat: "60-80%", label: "of associate time spent on contract review", body: "Junior associates working 18-hour days, buried in clause-by-clause review. Burning out. Leaving. And every departure costs you six figures in recruiting and training.", delay: "0ms" },
            { stat: "40-60", label: "clauses per commercial contract", body: "Three to five contracts per day. Every clause a potential landmine. Miss one uncapped indemnity in a $500M deal and the partnership meeting won't be about bonuses.", delay: "150ms" },
            { stat: "$0", label: "institutional knowledge retained when associates leave", body: "Your best associate just gave notice. Their instinct for spotting bad clauses, the firm's unwritten standards, years of deal experience — walking out the door. Again.", delay: "300ms" },
          ].map((card, i) => (
            <div key={i} className={`transition-all duration-700 ${show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{ transitionDelay: card.delay }}>
              <p className="text-5xl font-bold text-[#1B3A6B] mb-1">{card.stat}</p>
              <p className="text-sm font-semibold text-[#C8A84B] uppercase tracking-wide mb-4" style={{ fontFamily: "system-ui, sans-serif" }}>{card.label}</p>
              <p className="text-sm text-gray-500 leading-relaxed" style={{ fontFamily: "system-ui, sans-serif" }}>{card.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function OutcomesSection() {
  const { ref, visible: show } = useAnimateOnScroll();
  return (
    <section id="outcomes" ref={ref} className="py-20 bg-white border-y border-gray-100">
      <div className="max-w-5xl mx-auto px-6">
        <div className={`grid md:grid-cols-3 gap-12 transition-all duration-700 ${show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {[
            { number: "5 min", headline: "Upload a 40-page MSA. Get a redlined Word doc back.", body: "Every clause analyzed, flagged, and revised — with redlined revisions ready to send to opposing counsel." },
            { number: "3 docs", headline: "Upload related contracts. See the conflicts between them.", body: "Your MSA says one thing, your SOW says another. We find the gaps and inconsistencies no one has time to cross-reference." },
            { number: "Day 1", headline: "Your firm's standards built in. Every review consistent.", body: "New associates produce partner-quality reviews from their first week. Your institutional knowledge stays — even when people don't." },
          ].map((item, i) => (
            <div key={i} className="text-center" style={{ transitionDelay: `${i * 150}ms` }}>
              <p className="text-4xl font-bold text-[#C8A84B] mb-3">{item.number}</p>
              <h3 className="text-lg text-[#1B3A6B] mb-3 leading-snug">{item.headline}</h3>
              <p className="text-sm text-gray-500 leading-relaxed" style={{ fontFamily: "system-ui, sans-serif" }}>{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FeaturesSection() {
  const { ref, visible: show } = useAnimateOnScroll();
  return (
    <section id="features" ref={ref} className="py-24 bg-[#F8F9FB]">
      <div className="max-w-5xl mx-auto px-6">
        <div className={`text-center mb-16 transition-all duration-700 ${show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-sm tracking-[0.2em] uppercase text-[#C8A84B] mb-3" style={{ fontFamily: "system-ui, sans-serif" }}>What Contract Lucidity Does</p>
          <h2 className="text-3xl md:text-4xl text-[#1B3A6B]">
            Comprehensive analysis.<br />Deliberate review. Actionable output.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          {[
            { title: "Clause-by-Clause Analysis", body: "Every clause rated RED, YELLOW, or GREEN against market standards. Deviation analysis, business impact assessment, and specific redline suggestions — not summaries, not extraction, actual analysis." },
            { title: "Cross-Document Intelligence", body: "Upload your MSA, SOW, and NDA together. AI identifies conflicts between documents, coverage gaps, and alignment issues that no attorney has time to cross-reference manually." },
            { title: "AI-Assisted Revision", body: "Click 'Enhance with AI' and the platform rewrites the clause based on the deviation, standard position, and your firm's playbook. Highlight a sentence to rewrite just that part. Edit manually. Accept when it's right." },
            { title: "Word Document Generation", body: "Generate a Word document with redlined revisions — original text in strikethrough, revised language underlined, deferred items flagged. The format every attorney already works in. Send it to opposing counsel today." },
            { title: "Institutional Playbook", body: "Your firm's position standards, risk thresholds, terminology, and clause templates — injected into every analysis. New associates produce reviews aligned with partner expectations from day one." },
            { title: "Obligation Tracking", body: "Deadlines, responsible parties, recurring obligations — extracted automatically with priority ranking. Track status from upcoming to completed. Never miss a renewal or compliance date." },
          ].map((feature, i) => (
            <div key={i} className={`bg-white rounded-lg p-8 border border-gray-100 transition-all duration-700 ${show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{ transitionDelay: `${i * 80}ms` }}>
              <h3 className="text-lg text-[#1B3A6B] mb-3" style={{ fontFamily: "Georgia, serif" }}>{feature.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed" style={{ fontFamily: "system-ui, sans-serif" }}>{feature.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function DifferentiatorSection() {
  const { ref, visible: show } = useAnimateOnScroll();
  return (
    <section id="differentiator" ref={ref} className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <div className={`transition-all duration-700 ${show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="bg-[#1B3A6B] rounded-lg overflow-hidden">
            <img src="/images/differentiator-collab.webp" alt="Attorneys collaborating in a modern office" className="w-full h-48 object-cover opacity-40" loading="lazy" />
            <div className="p-12 text-center -mt-12 relative">
              <p className="text-sm tracking-[0.2em] uppercase text-[#C8A84B] mb-4" style={{ fontFamily: "system-ui, sans-serif" }}>The Difference</p>
              <h2 className="text-3xl md:text-4xl text-white leading-snug mb-6">
                AI that assists your attorneys.<br />
                Not AI that replaces them.
              </h2>
              <p className="text-white/60 max-w-2xl mx-auto leading-relaxed mb-8" style={{ fontFamily: "system-ui, sans-serif" }}>
                Every AI suggestion requires explicit attorney approval. Every clause gets a deliberate decision — address, accept, or defer. The output is a Word document with redlined revisions, not a chat response. Your attorneys stay in control. The AI makes them faster.
              </p>
              <div className="inline-block h-px w-16 bg-[#C8A84B]" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function HowItWorksSection() {
  const { ref, visible: show } = useAnimateOnScroll();
  return (
    <section id="how-it-works" ref={ref} className="py-24 bg-[#F8F9FB]">
      <div className="max-w-4xl mx-auto px-6">
        <div className={`text-center mb-16 transition-all duration-700 ${show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-sm tracking-[0.2em] uppercase text-[#C8A84B] mb-3" style={{ fontFamily: "system-ui, sans-serif" }}>How It Works</p>
          <h2 className="text-3xl md:text-4xl text-[#1B3A6B]">Three steps. Five minutes. Every clause.</h2>
        </div>
        <div className="space-y-16">
          {[
            { step: "01", title: "Upload your contract", body: "PDF, Word, RTF, or even a photograph of a printed document. The platform extracts text, classifies the agreement type, and applies your firm's playbook standards automatically." },
            { step: "02", title: "Review each finding with AI assistance", body: "Every clause is analyzed against market standards. For each flagged clause, you decide: address it, accept it as-is, or defer for later. If you choose to address it, the AI drafts revised language that you can edit, enhance, or rewrite entirely." },
            { step: "03", title: "Generate a revised document and send it today", body: "Click one button. Download a Word document with redlined revisions — original text struck through, your revisions underlined, deferred items commented. The format opposing counsel expects. Ready to send." },
          ].map((item, i) => (
            <div key={i} className={`flex gap-8 items-start transition-all duration-700 ${show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{ transitionDelay: `${i * 200}ms` }}>
              <span className="text-6xl font-bold text-[#1B3A6B]/10 shrink-0 leading-none">{item.step}</span>
              <div>
                <h3 className="text-xl text-[#1B3A6B] mb-3">{item.title}</h3>
                <p className="text-gray-500 leading-relaxed" style={{ fontFamily: "system-ui, sans-serif" }}>{item.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CtaSection() {
  const { ref, visible: show } = useAnimateOnScroll();
  return (
    <section id="cta" ref={ref} className="py-24 bg-[#C8A84B]">
      <div className="max-w-3xl mx-auto px-6">
        <div className={`transition-all duration-700 ${show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="bg-white rounded-xl border-2 border-[#1B3A6B] p-12 text-center shadow-lg">
            <h2 className="text-3xl md:text-4xl text-[#1B3A6B] mb-4">
              Ready to see what your contracts are really saying?
            </h2>
            <p className="text-gray-500 mb-10 max-w-xl mx-auto" style={{ fontFamily: "system-ui, sans-serif" }}>
              Upload a real contract. See clause-by-clause analysis. Review every finding. Generate a revised document. All in under 5 minutes.
            </p>
            <a
              href="/demo"
              className="inline-block px-10 py-4 text-sm font-semibold bg-[#1B3A6B] text-white rounded hover:bg-white hover:text-[#1B3A6B] hover:ring-2 hover:ring-[#1B3A6B] transition-all"
              style={{ fontFamily: "system-ui, sans-serif" }}
            >
              Request a Demo
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
